import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import { ConfirmAlert } from 'v1/global/components/clink-alert';
import flag from 'v2/helpers/flags';
import i18next from 'v2/helpers/i18n';
import SendButtonNew from './SendButtonNew';

const getSubcontractorEmailFromMeta = (subcontractor, aid) => {
  const subcontractorEmail = subcontractor?.email;

  if (!subcontractor?.meta) {
    return subcontractorEmail;
  }

  let parsedMeta = false;
  try {
    parsedMeta = JSON.parse(subcontractor.meta);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Invalid JSON in subcontractor meta');
    return subcontractorEmail;
  }

  if (
    typeof parsedMeta === 'object' &&
    parsedMeta &&
    aid &&
    Object.hasOwn(parsedMeta, String(aid))
  ) {
    return parsedMeta[String(aid)]?.email || subcontractorEmail;
  }

  return subcontractorEmail;
};

const getConfirmOptions = ({
  subId,
  isLoi,
  handleSend,
  subName,
  subEmail,
}) => {
  if (!subId) {
    return {
      handleClick: null,
      title: 'Error',
      cancelLabel: 'Close',
      message: isLoi
        ? i18next.t('send-letter-of-intent-missing-subcontractor')
        : "We don't have subcontractor data to send this order",
      className: 'send-order-confirm',
    };
  }

  return {
    handleClick: handleSend,
    title: isLoi
      ? i18next.t('sending-letter-of-intent')
      : 'Sending order',
    cancelLabel: 'No',
    message: isLoi
      ? i18next.t('send-letter-of-intent-confirm', {
          name: subName,
          email: subEmail,
        })
      : `You're about to Send this order to ${subName} and ${subEmail} - would you like to proceed?`,
    className: 'send-order-confirm',
  };
};

const SendButton = (propsComp) => {
  if (flag('ENHANCE_SUPPLY_CHAIN_BUTTON')) {
    return <SendButtonNew {...propsComp} />;
  }

  const {
    info,
    disabled,
    handleSend,
    docType,
    subcontractor,
    docusignSendEmail,
    children,
  } = propsComp;

  if (docType === 'tender') {
    return (
      <Button
        disabled={disabled}
        onClick={handleSend}
        variant="contained"
        color="success"
        data-testid="document-creator-send-btn"
      >
        {children}
      </Button>
    );
  }

  const aid = info?.id || 0;
  const subId = subcontractor && Number(subcontractor.id);
  const subcontractorEmail = getSubcontractorEmailFromMeta(subcontractor, aid);
  const subEmail =
    (docusignSendEmail && docusignSendEmail.email) ||
    subcontractorEmail ||
    'No Email';
  const subName = subcontractor?.name || 'Unknown';

  const props = { disabled, children, variant: 'contained', color: 'success', 'data-testid': 'document-creator-send-btn' };
  const handleClick = !subId ? null : handleSend;
  const isLoi = docType === 'loi';
  const title = !subId
    ? 'Error'
    : isLoi
      ? i18next.t('sending-letter-of-intent')
      : 'Sending order';
  const cancelLabel = !subId ? 'Close' : 'No';
  const message = subId
    ? isLoi
      ? i18next.t('send-letter-of-intent-confirm', {
          name: subName,
          email: subEmail,
        })
      : `You're about to Send this order to ${subName} and ${subEmail} - would you like to proceed?`
    : isLoi
      ? i18next.t('send-letter-of-intent-missing-subcontractor')
      : "We don't have subcontractor data to send this order";
  // const options = {
  //   handleClick,
  //   title,
  //   message,
  //   className: 'send-order-confirm',
  //   cancelLabel,
  // };
  const options = getConfirmOptions({
    subId,
    isLoi: docType === 'loi',
    handleSend,
    subName,
    subEmail,
  });

  return <ConfirmAlert Component={Button} props={props} options={options} />;
};

SendButton.propTypes = {
  info: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  disabled: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  handleSend: PropTypes.func.isRequired,
  docType: PropTypes.string,
  subcontractor: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
    meta: PropTypes.string,
  }),
  docusignSendEmail: PropTypes.oneOfType([
    PropTypes.shape({
      email: PropTypes.string,
    }),
    PropTypes.bool,
  ]),
  children: PropTypes.node.isRequired,
};

SendButton.defaultProps = {
  info: null,
  disabled: false,
  docType: '',
  subcontractor: null,
  docusignSendEmail: null,
};

export default SendButton;
